import {ListItem} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';
import {useLocation} from 'wouter';

import {DocumentTypeLabel} from '../../../../components/document-type-label';
import {removeElementRef, saveElementRef} from '../../../../components/scroll-to-element';

import styles from './document-link.module.css';
import {DocumentEditStatusIcon, StatusIconSettings} from '../doc-status-icon/document-status-icon';

function isModifiedEvent(event: MouseEvent | KeyboardEvent) {
    return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

export interface Props {
    doc: EditModeDoc;
    statusIconSettings: StatusIconSettings;

    disabled?: boolean;
    active?: boolean;
    prepend?: React.ReactNode;
    /** Determines whether current link can be scrolled into view */
    canScrollIntoView?: boolean;
    offset?: number;
    marginLeft?: number;
    additionalClickHandler?: () => void;
}

// eslint-disable-next-line react/display-name
export const DocumentLink = React.memo(
    ({
        doc,
        active,
        statusIconSettings,
        disabled = false,
        canScrollIntoView = false,
        prepend,
        offset,
        marginLeft,
        additionalClickHandler
    }: Props) => {
        const [, navigate] = useLocation();
        const documentPath = `/${doc.accession}`;
        const documentHref = `${window.location.origin}/documents/edit/${doc.accession}`;

        const liElement = React.useRef<HTMLElement>(null);

        React.useEffect(() => {
            if (canScrollIntoView && active && liElement.current) {
                saveElementRef(liElement, doc.accession);
            }

            // teardown
            return () => {
                removeElementRef(doc.accession);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [active]);

        const label = (
            <span className={styles.labelContainer}>
                <DocumentTypeLabel type={doc.type} selected={active} readOnly={doc.readOnly} />
            </span>
        );

        const handleClick = React.useCallback(
            ({nativeEvent}: {nativeEvent: MouseEvent | KeyboardEvent}) => {
                if (
                    (nativeEvent instanceof MouseEvent || nativeEvent instanceof KeyboardEvent) &&
                    !nativeEvent.defaultPrevented && // onClick prevented default
                    !isModifiedEvent(nativeEvent) // ignore clicks with modifier keys
                ) {
                    nativeEvent.preventDefault();
                    navigate(documentPath);
                    additionalClickHandler?.();
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documentPath, additionalClickHandler]
        );

        return (
            <ListItem
                data-qa-id="document"
                href={documentHref}
                disabled={disabled}
                onClick={handleClick}
                prepend={
                    <>
                        {prepend}
                        {label}
                    </>
                }
                append={<DocumentEditStatusIcon document={doc} {...statusIconSettings} />}
                interactive
                className={classNames(styles.root, {
                    [styles.active]: active
                })}
                ref={liElement}
                titleContentProps={
                    {
                        'data-qa-name': 'document name'
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any
                }
                style={{
                    paddingLeft: offset,
                    marginLeft
                }}
            >
                {doc.name}
            </ListItem>
        );
    }
);
